import styled, { css, keyframes } from 'styled-components'
import { ResetButtonStyles } from './buttons.styles'
import { BREAKPOINTS } from '@at/window-utils/lib/responsive-utils/match-media.util'
import { ThemeSelectors, TypeToledo } from '@at/design-system-foundation'
import { ModalSize } from './modal'
import { LinkStyles } from '@at/design-system-elements'

const handleModalSize = (size: ModalSize) => {
    const modalSpacing = css`
        margin: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px;
        height: calc(100% - ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 4}px);
        width: auto;
    `
    switch (size) {
        case ModalSize.FULL_SCREEN:
            return css`
                @media (min-width: ${BREAKPOINTS.MEDIUM}px) {
                    max-width: 596px;
                    margin: 50px auto;
                    height: calc(100% - 100px);
                }
                @media (max-width: ${BREAKPOINTS.MEDIUM}px) {
                    & > section {
                        border-radius: 0;
                    }
                }
                height: 100%;
            `
        case ModalSize.LARGE:
            return css`
                @media (min-width: ${BREAKPOINTS.MEDIUM}px) {
                    max-width: 800px;
                    margin: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px auto;
                }
                ${modalSpacing}
            `
        case ModalSize.SMALL:
            return css`
                @media (min-width: ${BREAKPOINTS.MEDIUM}px) {
                    max-width: 320px;
                    margin: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px auto;
                }
                ${modalSpacing}
            `
        default:
            // MEDIUM DEFAULT
            return css`
                @media (min-width: ${BREAKPOINTS.MEDIUM}px) {
                    max-width: 550px;
                    margin: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px auto;
                }
                ${modalSpacing}
            `
    }
}

const slideIn = keyframes`
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
`

const slideOut = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(120%);
    }
`

export const ModalOverlay = styled.div`
    position: fixed;
    z-index: 1000;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) => theme.color.bg.blanket};
    outline: 0;
    display: block;
`

export const ModalDialog = styled.div<{ modalSize: ModalSize }>`
    ${({ modalSize }) => {
        return css`
            ${handleModalSize(modalSize)}
            pointer-events: none;
            position: relative;
        `
    }}
`

export const Modal = styled.section<{ isModalShowing: boolean }>`
    ${({ theme, isModalShowing }) => {
        return css`
            background-clip: padding-box;
            background-color: ${theme.color.bgSurface.default};
            border-radius: 16px;
            display: flex;
            flex-direction: column;
            max-height: 100%;
            outline: 0;
            overflow: hidden;
            pointer-events: auto;
            position: relative;
            width: 100%;
            animation: ${isModalShowing ? slideIn : slideOut} 0.3s ease-out forwards;
        `
    }}
`

const ModalHorizontalPaddingBreakpoints = styled.div`
    @media (min-width: 431px) {
        padding-left: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 5}px;
        padding-right: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 5}px;
    }
    @media (max-width: 430px) {
        padding-left: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 3}px;
        padding-right: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 3}px;
    }
    @media (max-width: 374px) {
        padding-left: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px;
        padding-right: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px;
    }
`

export const ModalHeader = styled(ModalHorizontalPaddingBreakpoints)`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid ${({ theme }) => theme.color.border.default};
    padding-top: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px;
    padding-bottom: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px;
    border-top-left-radius: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) / 2}px;
    border-top-right-radius: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) / 2}px;
`

export const ModalBody = styled(ModalHorizontalPaddingBreakpoints)`
    flex: 1 1 auto;
    overflow-y: auto;
    padding-top: 0;
    padding-bottom: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 2}px;
    position: relative;
`

export const ModalFooter = styled(ModalHorizontalPaddingBreakpoints)`
    border-top: 1px solid ${({ theme }) => theme.color.border.default};
`

export const ModalContent = styled.div`
    display: block;
    width: 100%;
`

export const ModalButton = styled(ResetButtonStyles)`
    ${({ disabled }) => css`
        ${({ theme }) => LinkStyles(theme)};
        border: none;
        visibility: ${!disabled ? 'visible' : 'hidden'};

        .icon-svg {
            display: inline-block;
        }

        svg {
            fill: currentColor;
        }
    `}
`

export const ModalTitle = styled(TypeToledo)`
    font-variation-settings: 'wght' 400;
`
